import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WaitlistModal from "../components/WaitlistModal";

const DeleteAccount: React.FC = () => {
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  return (
    <div className="min-h-screen bg-[#000000] text-white">
      <Helmet>
        <title>Delete Account | AmmoPantryAI</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header onJoinClick={() => setShowWaitlistModal(true)} />
      <WaitlistModal
        open={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
      />
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-extrabold mb-6">Delete Your Account</h1>
        <p className="mb-4">Last updated: December 2025</p>
        
        <div className="bg-[#1a1a1a] border-l-4 border-[#3b82f6] p-6 mb-8 rounded-r-lg">
          <p className="text-lg font-semibold text-white mb-2">
            Request Account Deletion from AmmoPantryAI
          </p>
          <p className="text-[#a3a3a3]">
            If you would like to request that your account and associated data be deleted from AmmoPantryAI
            (operated by Edifi Ventures, LLC), please follow the steps below.
          </p>
        </div>

        <div className="bg-[#1a1a1a] border-l-4 border-[#16a34a] p-6 mb-8 rounded-r-lg">
          <p className="text-lg font-semibold text-white mb-2">
            Delete Your Account Directly in the App
          </p>
          <p className="text-[#a3a3a3] mb-2">
            You can delete your account directly within the AmmoPantryAI app:
          </p>
          <ol className="list-decimal list-inside ml-4 space-y-1 text-[#a3a3a3]">
            <li>Open the AmmoPantryAI app on your device</li>
            <li>Navigate to <strong>Settings</strong></li>
            <li>Select <strong>Manage Account</strong></li>
            <li>Follow the prompts to delete your account and associated data</li>
          </ol>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">How to Request Account Deletion</h2>
        <p className="mb-4">
          To request deletion of your account and associated data from AmmoPantryAI, please follow these steps:
        </p>
        
        <ol className="list-decimal list-inside mb-6 ml-4 space-y-4">
          <li className="mb-2">
            <strong>Email us at:</strong> Send a deletion request to{" "}
            <a href="mailto:ammopantry@gmail.com" className="text-[#3b82f6] underline font-semibold">
              ammopantry@gmail.com
            </a>
          </li>
          <li className="mb-2">
            <strong>Include the following information in your email:</strong>
            <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
              <li>Your email address associated with your account (if you contacted us previously)</li>
              <li>Subject line: "Account Deletion Request"</li>
              <li>Your full name</li>
              <li>Confirmation that you want to delete your account and all associated data</li>
            </ul>
          </li>
          <li className="mb-2">
            <strong>We will process your request:</strong> We will review and process your deletion request 
            within 30 days of receipt. You will receive a confirmation email once your data has been deleted.
          </li>
        </ol>

        <h2 className="text-2xl font-bold mt-8 mb-4">What Data Will Be Deleted</h2>
        <p className="mb-4">
          When you request account deletion, AmmoPantryAI will delete the following data:
        </p>
        <ul className="list-disc list-inside mb-6 ml-8 space-y-2">
          <li>
            <strong>Personal Information:</strong> Any email addresses or contact information you provided 
            when contacting us for support or inquiries
          </li>
          <li>
            <strong>Support Records:</strong> Any correspondence or support tickets associated with your account
          </li>
          <li>
            <strong>Usage Data:</strong> Any anonymized usage data that can be linked to your account (if applicable)
          </li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">What Data Is Stored Locally</h2>
        <p className="mb-4">
          <strong>Important:</strong> AmmoPantryAI stores your app data (inventory, usage details, etc.) locally 
          on your device. This data is not stored on our servers. To delete this data:
        </p>
        <ul className="list-disc list-inside mb-6 ml-8 space-y-2">
          <li>Delete the app data directly from within the AmmoPantryAI app, or</li>
          <li>Uninstall the AmmoPantryAI app from your device</li>
        </ul>
        <p className="mb-4">
          <strong>Note about Cloud Backups:</strong> If you have Cloud Backup enabled on your device, your app 
          data may be automatically backed up to your personal Cloud account (e.g., iCloud). This backup is 
          controlled by your device settings and Apple's Cloud service, not by AmmoPantryAI. To delete data from 
          Cloud backups, you must manage this through your device settings.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">Data Retention Period</h2>
        <p className="mb-4">
          After you submit a deletion request:
        </p>
        <ul className="list-disc list-inside mb-6 ml-8 space-y-2">
          <li>
            <strong>Processing Time:</strong> We will process your deletion request within 30 days of receipt
          </li>
          <li>
            <strong>Data Deletion:</strong> Once processed, your personal data will be permanently deleted from 
            our systems
          </li>
          <li>
            <strong>Legal Requirements:</strong> We may be required to retain certain information for a longer 
            period if we have a legal obligation or lawful basis to do so (e.g., for legal compliance, dispute 
            resolution, or enforcement of legal agreements). In such cases, we will only retain the minimum 
            amount of data necessary and will delete it as soon as legally permitted.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">Additional Information</h2>
        <p className="mb-4">
          <strong>About AmmoPantryAI:</strong> AmmoPantryAI is operated by Edifi Ventures, LLC. AmmoPantryAI 
          is a D.B.A. (Doing Business As) of Edifi Ventures, LLC, a Limited Liability Company certified in the 
          state of Utah, United States.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">Contact Us</h2>
        <p className="mb-4">
          If you have any questions about account deletion or this process, please contact us at{" "}
          <a href="mailto:ammopantry@gmail.com" className="text-[#3b82f6] underline">
            ammopantry@gmail.com
          </a>
          .
        </p>
        <p className="mb-4">
          For more information about how we handle your data, please review our{" "}
          <a href="/privacy-policy" className="text-[#3b82f6] underline">
            Privacy Policy
          </a>
          .
        </p>
      </div>
      <Footer
        onSignUpClick={() => setShowWaitlistModal(true)}
      />
    </div>
  );
};

export default DeleteAccount;

